import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Presentation, Send, CheckCircle2, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function DealRoomPage() {
  const { token } = useParams();
  const [room, setRoom] = useState(null);
  const [missing, setMissing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/deal-rooms/${token}`)
      .then((r) => setRoom(r.data))
      .catch(() => setMissing(true));
  }, [token]);

  // Reading-time heartbeat — feeds the rep's deal-room analytics
  useEffect(() => {
    const ping = () => {
      if (document.visibilityState !== "visible") return;
      api.post(`/deal-rooms/${token}/ping`, { seconds: 20 }).catch(() => {});
    };
    const t = setInterval(ping, 20000);
    const onHide = () => {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms/${token}/ping`,
          new Blob([JSON.stringify({ seconds: 20 })], { type: "application/json" })
        );
      }
    };
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(t); window.removeEventListener("pagehide", onHide); };
  }, [token]);

  const enquire = async () => {
    setBusy(true);
    try {
      await api.post(`/deal-rooms/${token}/enquiry`, { name: name.trim(), email: email.trim().toLowerCase(), message: message.trim() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return (
      <div data-testid="deal-room-missing" className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="border border-white/10 bg-black/60 p-8 text-center max-w-sm">
          <ShieldAlert className="w-8 h-8 text-nc-orange mx-auto mb-4" />
          <p className="font-sans font-bold uppercase text-sm text-zinc-100">Proposal link inactive</p>
          <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">This deal room has been taken offline or never existed.</p>
          <Link to="/" className="font-mono text-[10px] tracking-[0.25em] text-nc-orange uppercase mt-4 inline-block">Visit specpixel.com</Link>
        </div>
      </div>
    );
  }

  if (!room) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><p className="font-mono text-xs text-zinc-600 animate-pulse">Loading proposal…</p></div>;
  }

  return (
    <div data-testid="deal-room-page" className="min-h-screen bg-[#050505] text-zinc-100 selection:bg-nc-orange/30">
      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase mb-3 flex items-center gap-2">
            <Presentation className="w-3.5 h-3.5" /> Specpixel Entertainment // Project Proposal
          </p>
          <h1 data-testid="deal-room-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">{room.title}</h1>
          <p className="font-mono text-xs text-zinc-500 mt-4">
            Prepared for <span className="text-zinc-200">{room.client_name}</span> by <span className="text-nc-orange">{room.rep_name}</span>
            {room.engine_type && <> // {room.engine_type}</>}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 flex flex-col gap-8">
        {room.intro && (
          <div data-testid="deal-room-intro" className="border-l-2 border-nc-orange pl-5">
            <p className="font-mono text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{room.intro}</p>
          </div>
        )}

        {room.items.length > 0 && (
          <div data-testid="deal-room-scope" className="border border-white/10">
            <div className="border-b border-white/10 px-5 py-3 bg-white/5 font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase">Scope of Work</div>
            {room.items.map((item, i) => (
              <div key={i} data-testid={`deal-room-item-${i + 1}`} className="grid grid-cols-[1fr_auto] gap-4 border-b border-white/5 px-5 py-4">
                <div>
                  <p className="font-sans font-semibold text-sm text-zinc-100">{item.service}</p>
                  {item.description && <p className="font-mono text-[11px] text-zinc-500 mt-1 leading-relaxed">{item.description}</p>}
                </div>
                <p className="font-mono text-sm text-zinc-100 shrink-0">AUD {item.price}</p>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_auto] gap-4 px-5 py-4 bg-nc-orange/5">
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Total Investment</p>
              <p data-testid="deal-room-total" className="font-sans font-bold text-lg text-nc-orange">AUD {room.total}</p>
            </div>
          </div>
        )}

        <div data-testid="deal-room-enquiry" className="border border-nc-orange/30 bg-nc-orange/5 p-6 flex flex-col gap-4">
          <div>
            <p className="font-sans font-bold uppercase text-sm text-zinc-100">Questions about this proposal?</p>
            <p className="font-mono text-[11px] text-zinc-500 mt-1 leading-relaxed">Message {room.rep_name} directly — your enquiry opens a live chat channel with the studio.</p>
          </div>
          {sent ? (
            <div data-testid="deal-room-sent" className="border border-nc-green/40 bg-nc-green/5 p-5 text-center flex flex-col gap-2">
              <CheckCircle2 className="w-6 h-6 text-nc-green mx-auto" />
              <p className="font-sans font-bold text-sm text-zinc-100">Message received</p>
              <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">{room.rep_name} has your enquiry — expect a reply shortly. You can also open the live chat on our homepage.</p>
              <Link to="/" className="font-mono text-[10px] tracking-[0.25em] text-nc-green uppercase mt-1">Open specpixel.com chat</Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Your name</label>
                  <input data-testid="deal-room-name-input" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={60} />
                </div>
                <div>
                  <label className={labelCls}>Your email</label>
                  <input data-testid="deal-room-email-input" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Message</label>
                <textarea data-testid="deal-room-message-input" rows={3} className={`${inputCls} resize-y`} placeholder="Ask about timelines, revisions, technical details…" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} />
              </div>
              <button data-testid="deal-room-enquiry-submit" disabled={busy || name.trim().length < 2 || !email.includes("@") || message.trim().length < 3} onClick={enquire} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-3 flex items-center gap-2 transition-colors">
                <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : `Message ${room.rep_name}`}
              </button>
            </>
          )}
        </div>

        <p className="font-mono text-[9px] text-zinc-600 text-center pb-6">
          Powered by <Link to="/" className="text-nc-orange hover:text-zinc-100 transition-colors">Specpixel Entertainment</Link> — AAA outsourcing, engineered in Australia.
        </p>
      </main>
    </div>
  );
}
